import { useState, useEffect, useCallback, useRef } from 'react';
import { ticketService } from '@/services/ticketService';
import { useAuth } from '@/contexts/AuthContext';
import type { TicketNotificationItem } from '@/types/tickets';

// Bus simples para sincronizar os consumidores (sininho + badge da sidebar)
// quando o estado de leitura muda (marcar todas como lidas, abrir um ticket).
// Cada listener apenas refaz seu próprio fetch; o emit só é disparado por ações
// explícitas, então não há laço de atualização.
type BusListener = () => void;
const notificationListeners = new Set<BusListener>();
export function emitTicketNotificationsChanged(): void {
  notificationListeners.forEach((l) => {
    try { l(); } catch { /* noop */ }
  });
}
function useNotificationsBus(onChange: () => void): void {
  useEffect(() => {
    notificationListeners.add(onChange);
    return () => { notificationListeners.delete(onChange); };
  }, [onChange]);
}

/**
 * Hook das notificações de tickets (atribuições + menções).
 * Faz polling a cada 30s da feed e deriva a contagem de não-lidas.
 */
export const useTicketNotifications = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<TicketNotificationItem[]>([]);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  // Geração de requisição: respostas obsoletas (ex.: um poll lento que saiu antes
  // de "marcar como lida") são descartadas para não sobrescrever o estado novo.
  const reqIdRef = useRef(0);

  const fetchFeed = useCallback(async () => {
    if (!user?.id) {
      setItems([]);
      setCount(0);
      return;
    }
    const myId = ++reqIdRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const { items } = await ticketService.getNotifications();
      if (myId !== reqIdRef.current) return; // resposta obsoleta
      setItems(items);
      setCount(items.filter((i) => !i.is_read).length);
    } catch (err) {
      if (myId !== reqIdRef.current) return;
      console.error('[useTicketNotifications] Erro ao buscar notificações:', err);
      setError(err instanceof Error ? err : new Error('Erro ao buscar notificações'));
    } finally {
      if (myId === reqIdRef.current) setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(() => fetchFeed(), 30000);
    return () => clearInterval(interval);
  }, [user?.id, fetchFeed]);

  const refresh = useCallback(() => fetchFeed(), [fetchFeed]);

  // Sincroniza com ações de outros consumidores (ex.: abrir ticket marca lido).
  useNotificationsBus(fetchFeed);

  const markAllRead = useCallback(async () => {
    // Otimista: zera na hora e invalida qualquer fetch em voo (poll lento) para
    // que ele não traga os itens não-lidos de volta ao resolver.
    reqIdRef.current++;
    setItems((prev) => prev.map((i) => ({ ...i, is_read: true })));
    setCount(0);
    try {
      await ticketService.markAllNotificationsRead();
    } finally {
      await fetchFeed();
      emitTicketNotificationsChanged(); // atualiza também o badge da sidebar
    }
  }, [fetchFeed]);

  return {
    items,
    count,
    isLoading,
    error,
    refresh,
    markAllRead,
    hasNotifications: count > 0,
  };
};

/**
 * Hook leve para a sidebar — faz polling apenas do contador de não-lidas (GET /tickets/notifications/count).
 * Evita carregar a feed completa em todos os consumidores.
 */
export const useTicketNotificationsCount = () => {
  const { user } = useAuth();
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const reqIdRef = useRef(0);

  const fetchCount = useCallback(async () => {
    if (!user?.id) { setCount(0); return; }
    const myId = ++reqIdRef.current;
    try {
      setIsLoading(true);
      const c = await ticketService.getUnviewedCount();
      if (myId !== reqIdRef.current) return; // resposta obsoleta
      setCount(c || 0);
    } catch (err) {
      if (myId !== reqIdRef.current) return;
      console.error('[useTicketNotificationsCount] Erro ao buscar contador:', err);
    } finally {
      if (myId === reqIdRef.current) setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchCount(); }, [fetchCount]);
  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(() => fetchCount(), 30000);
    return () => clearInterval(interval);
  }, [user?.id, fetchCount]);

  // Atualiza na hora quando o sininho marca lidas / abre um ticket.
  useNotificationsBus(fetchCount);

  const refresh = useCallback(() => fetchCount(), [fetchCount]);
  return { count, isLoading, refresh, hasNotifications: count > 0 };
};
